var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { getSearchProvider } from './providers';
import AppConfig from '../config/config';
export function useSearchProvider() {
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [filters, setFilters] = useState([]);
    const [meta, setMeta] = useState(null);
    const [sortOptions, setSortOptions] = useState([]);
    const [provider] = useState(() => getSearchProvider());
    const [isMobile, setIsMobile] = useState(false);
    const debounceTimeoutRef = useRef(null);
    const [searchState, setSearchState] = useState({
        query: '',
        currentSort: 'relevance',
        appliedFilters: {},
        currentPage: 1,
    });
    // Detect mobile device and update page size accordingly
    useEffect(() => {
        const checkMobile = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;
            const userAgent = navigator.userAgent;
            const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
            const isMobileWidth = width <= 768;
            const isMobileOrientation = width < height && width < 1024; // Portrait mode on tablets
            const mobile = isMobileWidth || (isMobileUserAgent && isMobileOrientation);
            setIsMobile(mobile);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    // Get responsive page size
    const getPageSize = () => {
        // Calculate mobile detection inline to avoid timing issues
        const width = window.innerWidth;
        const height = window.innerHeight;
        const userAgent = navigator.userAgent;
        const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
        const isMobileWidth = width <= 768;
        const isMobileOrientation = width < height && width < 1024;
        const isMobileDevice = isMobileWidth || (isMobileUserAgent && isMobileOrientation);
        return isMobileDevice ? AppConfig.page.mobileSize : AppConfig.page.defaultSize;
    };
    const runSearch = (query_1, ...args_1) => __awaiter(this, [query_1, ...args_1], void 0, function* (query, options = {}) {
        setLoading(true);
        try {
            // Use responsive page size
            const pageSize = options.pageSize || getPageSize();
            yield provider.search(query, Object.assign(Object.assign({}, options), { pageSize }));
            setProducts(provider.getProducts());
            setFilters(provider.getFilters());
            setMeta(provider.getMeta());
            setSortOptions(provider.getSortOptions());
        }
        catch (error) {
            console.error('Search failed:', error);
        }
        finally {
            setLoading(false);
        }
    });
    const debouncedSearch = useCallback((query, options = {}) => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        debounceTimeoutRef.current = setTimeout(() => {
            runSearch(query, Object.assign(Object.assign({}, options), { pageSize: getPageSize() }));
        }, 300);
    }, [isMobile] // Add isMobile as dependency
    );
    const updateUrlParams = useCallback(({ query, currentSort, appliedFilters, currentPage }) => {
        const urlParams = new URLSearchParams();
        if (currentSort) {
            urlParams.set('sort', currentSort);
        }
        if (query) {
            urlParams.set('q', query);
        }
        if (currentPage && currentPage > 1) {
            urlParams.set('page', currentPage.toString());
        }
        Object.entries(appliedFilters).forEach(([key, values]) => {
            values.forEach((value) => {
                urlParams.append(key, value);
            });
        });
        const paramString = urlParams.toString();
        const newUrl = paramString
            ? `${window.location.pathname}?${paramString}`
            : window.location.pathname;
        window.history.replaceState({}, '', newUrl);
    }, []);
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const queryParam = urlParams.get('q');
        const sortParam = urlParams.get('sort');
        const pageParam = parseInt(urlParams.get('page') || '1', 10);
        const initialFilters = {};
        urlParams.forEach((value, key) => {
            if (key !== 'q' && key !== 'sort' && key !== 'page') {
                if (!initialFilters[key]) {
                    initialFilters[key] = [];
                }
                initialFilters[key].push(value);
            }
        });
        const newSearchState = {
            query: queryParam || '',
            currentSort: sortParam || '',
            appliedFilters: initialFilters,
            currentPage: pageParam,
        };
        setSearchState(newSearchState);
        updateUrlParams(newSearchState);
        runSearch(newSearchState.query, {
            page: pageParam,
            sort: newSearchState.currentSort || undefined,
            filters: newSearchState.appliedFilters,
            pageSize: getPageSize(),
        });
    }, [isMobile]); // Add isMobile as dependency
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);
    const handleQuerySubmit = (newQuery) => {
        const newSearchState = Object.assign(Object.assign({}, searchState), { query: newQuery });
        setSearchState(newSearchState);
        updateUrlParams(newSearchState);
        debouncedSearch(newQuery, {
            sort: searchState.currentSort || undefined,
            filters: searchState.appliedFilters,
            pageSize: getPageSize(),
        });
    };
    const handleFilterChange = (filterId, value) => {
        setSearchState((prev) => {
            var _a, _b;
            const newFilters = Object.assign({}, prev.appliedFilters);
            if (filterId === 'price' || filterId === 'pf_p_price') {
                newFilters[filterId] = [value];
            }
            else {
                const filter = filters.find(f => f.id === filterId);
                const isSingleSelect = (filter === null || filter === void 0 ? void 0 : filter.selectType) === 'single';
                if (isSingleSelect) {
                    if ((_a = newFilters[filterId]) === null || _a === void 0 ? void 0 : _a.includes(value)) {
                        delete newFilters[filterId];
                    }
                    else {
                        newFilters[filterId] = [value];
                    }
                }
                else {
                    if ((_b = newFilters[filterId]) === null || _b === void 0 ? void 0 : _b.includes(value)) {
                        newFilters[filterId] = newFilters[filterId].filter((v) => v !== value);
                        if (newFilters[filterId].length === 0) {
                            delete newFilters[filterId];
                        }
                    }
                    else {
                        newFilters[filterId] = [...(newFilters[filterId] || []), value];
                    }
                }
            }
            const newSearchState = Object.assign(Object.assign({}, prev), { appliedFilters: newFilters, currentPage: 1 });
            updateUrlParams(newSearchState);
            runSearch(prev.query, {
                page: 1,
                sort: prev.currentSort || undefined,
                filters: newFilters,
                pageSize: getPageSize(),
            });
            return newSearchState;
        });
    };
    const handlePageChange = (page) => {
        const newSearchState = Object.assign(Object.assign({}, searchState), { currentPage: page });
        setSearchState(newSearchState);
        updateUrlParams(newSearchState);
        runSearch(searchState.query, {
            page,
            sort: searchState.currentSort || undefined,
            filters: searchState.appliedFilters,
            pageSize: getPageSize(),
        });
    };
    const handleSortChange = (sort) => {
        const newSearchState = Object.assign(Object.assign({}, searchState), { currentSort: sort, currentPage: 1 });
        setSearchState(newSearchState);
        updateUrlParams(newSearchState);
        runSearch(searchState.query, {
            page: 1,
            sort: sort || undefined,
            filters: searchState.appliedFilters,
            pageSize: getPageSize(),
        });
    };
    const resetFilters = () => {
        const newSearchState = Object.assign(Object.assign({}, searchState), { appliedFilters: {} });
        setSearchState(newSearchState);
        updateUrlParams(newSearchState);
        runSearch(searchState.query, {
            filters: {},
            pageSize: getPageSize(),
        });
    };
    const onRemoveFilter = (filterId, value) => {
        setSearchState((prev) => {
            const newFilters = Object.assign({}, prev.appliedFilters);
            if (filterId === 'price' || filterId === 'pf_p_price') {
                delete newFilters[filterId];
            }
            else {
                if (newFilters[filterId]) {
                    newFilters[filterId] = newFilters[filterId].filter((v) => v !== value);
                    if (newFilters[filterId].length === 0) {
                        delete newFilters[filterId];
                    }
                }
            }
            const newSearchState = Object.assign(Object.assign({}, prev), { appliedFilters: newFilters });
            updateUrlParams(newSearchState);
            runSearch(prev.query, {
                sort: prev.currentSort || undefined,
                filters: newFilters,
                pageSize: getPageSize(),
            });
            return newSearchState;
        });
    };
    return {
        loading,
        products,
        filters,
        meta,
        sortOptions,
        query: searchState.query,
        currentSort: searchState.currentSort,
        appliedFilters: searchState.appliedFilters,
        handleQuerySubmit,
        handleFilterChange,
        handlePageChange,
        handleSortChange,
        runSearch,
        resetFilters,
        onRemoveFilter,
    };
}
